import React from 'react'
import PropTypes from 'prop-types'
import { Badge, Ratio, Row, Col } from 'react-bootstrap'

import dynamic from 'next/dynamic'
import { useQuery } from '@apollo/client'

import fantasyStyles from './style.module.scss'
import { convertDate, dateCheck } from '@shared-submodule/utils'
// import TipsNote from '@shared/components/fantasyTips/tipsNote'
import { setMatchPlayer } from '@shared/libs/match-detail'
import useStatus from '@shared-submodule/sm-hooks/useStatus'
import { GET_TEAM_FORM } from '@graphql/match/match.query'
import { STATS } from '@graphql/series/stats.query'
import SeriesStats from './seriesStats'

const InfoBlock = dynamic(() => import('@shared/components/fantasyTips/infoBlock'))
const InfoList = dynamic(() => import('@shared/components/fantasyTips/infoList'))
const PlayerPicks = dynamic(() => import('@shared/components/fantasyTips/playerPicks'))
const PlayingXI = dynamic(() => import('@shared/components/fantasyTips/playingXI'))
const FantasyTeam = dynamic(() => import('@shared/components/fantasyTips/fantasyTeam'))
const CustomLink = dynamic(() => import('@shared/components/customLink'))
const TeamForm = dynamic(() => import('@shared/components/match/teamForm'))
const AvoidPlayer = dynamic(() => import('@shared/components/fantasyTips/avoidPlayer'))

function FantasyArticleContent({ data, playingXI, match, seriesData, isMobile = false }) {
  setMatchPlayer(playingXI || {})

  const fantasyArticleData = data
  const { sStatusStr } = useStatus(fantasyArticleData?.oMatch)
  const { data: teamFormA } = useQuery(GET_TEAM_FORM, {
    variables: { input: { iTeamId: match?.oTeamScoreA?.oTeam?._id, iMatchId: match?._id } }
  })
  const { data: teamFormB } = useQuery(GET_TEAM_FORM, {
    variables: { input: { iTeamId: match?.oTeamScoreB?.oTeam?._id, iMatchId: match?._id } }
  })
  const teamAFormInput = teamFormA?.getTeamForm
  const teamBFormInput = teamFormB?.getTeamForm

  const battingMostRuns = seriesData?.find(obj => obj.sType === 'batting_most_runs')
  const { data: statsDataBattingMostRuns } = useQuery(STATS, {
    variables: {
      input: {
        iSeriesId: match?.oSeries?._id,
        _id: battingMostRuns?._id
      }
    }
  })

  const battingBestStrikeRate = seriesData?.find(obj => obj.sType === 'batting_highest_strikerate')
  const { data: statsDataBattingBestStrikeRate } = useQuery(STATS, {
    variables: {
      input: {
        iSeriesId: match?.oSeries?._id,
        _id: battingBestStrikeRate?._id
      }
    }
  })

  const bowlingMostWickets = seriesData?.find(obj => obj.sType === 'bowling_top_wicket_takers')
  const { data: statsDataBowlingMostWickets } = useQuery(STATS, {
    variables: {
      input: {
        iSeriesId: match?.oSeries?._id,
        _id: bowlingMostWickets?._id
      }
    }
  })

  const bowlingBestEconomyRate = seriesData?.find(obj => obj.sType === 'bowling_best_economy_rates')
  const { data: statsDataBowlingBestEconomyRate } = useQuery(STATS, {
    variables: {
      input: {
        iSeriesId: match?.oSeries?._id,
        _id: bowlingBestEconomyRate?._id
      }
    }
  })

  return (
    <div className={fantasyStyles.fantasySection}>
      {sStatusStr === 'scheduled' && (
        <div className={'common-box pb-xs-0'}>
          <Row className={fantasyStyles.rowEqualWidth}>
            <Col className={`${fantasyStyles.colEqualWidth}`}>
              <p className="text-muted mb-1">Series</p>
              <p className={`${fantasyStyles.title} mb-0`}>{fantasyArticleData?.oMatch?.oSeries?.sTitle}</p>
            </Col>
            <Col className={`${fantasyStyles.colEqualWidth}`}>
              <p className="text-muted mb-1">{'Match Will Start On'}</p>
              <p className={`${fantasyStyles.title} mb-0`}>{convertDate(dateCheck(fantasyArticleData?.oMatch?.dStartDate))}</p>
            </Col>
            <Col className={`${fantasyStyles.colEqualWidth}`}>
              <p className="text-muted mb-1">Venue</p>
              <p className={`${fantasyStyles.title} mb-0`}>{fantasyArticleData?.oMatch?.oVenue?.sLocation}</p>
            </Col>
          </Row>
        </div>
      )}
      {fantasyArticleData?.sMatchPreview !== null && (
        <div className={'common-box mb-3'}>
          <InfoBlock fantasystyles={fantasyStyles} title={'Match Preview'} info={fantasyArticleData?.sMatchPreview} />
          {fantasyArticleData?.oOverview?.sPitchReport && (
            <InfoBlock fantasystyles={fantasyStyles} title={'Pitch Condition'} info={fantasyArticleData?.oOverview?.sPitchReport} />
          )}
        </div>
      )}
      {fantasyArticleData?.oOverview && <InfoList fantasystyles={fantasyStyles} overViewData={fantasyArticleData?.oOverview} />}
      {teamAFormInput?.length > 0 && teamBFormInput?.length > 0 && (
        <TeamForm teamA={teamAFormInput} teamB={teamBFormInput} matchDetail={match} activeKey={true} isMobile={isMobile} />
      )}
      {seriesData?.length > 0 && (
        <SeriesStats
          seriesData={seriesData}
          statsDataBattingMostRuns={statsDataBattingMostRuns}
          statsDataBattingBestStrikeRate={statsDataBattingBestStrikeRate}
          statsDataBowlingMostWickets={statsDataBowlingMostWickets}
          statsDataBowlingBestEconomyRate={statsDataBowlingBestEconomyRate}
        />
      )}

      {fantasyArticleData?.oOverview !== null && (
        <>
          <PlayingXI fantasystyles={fantasyStyles} probableXI={fantasyArticleData?.oOverview} />
        </>
      )}

      {data?.sVideoUrl && (
        <section className="common-section pb-0">
          <Ratio aspectRatio="16x9" className={`${fantasyStyles.iframeVideo} m-auto`}>
            <iframe
              width="560"
              height="315"
              src={data?.sVideoUrl}
              title="YouTube video player"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen></iframe>
          </Ratio>
        </section>
      )}
      <PlayerPicks fantasystyles={fantasyStyles} playerPicksData={fantasyArticleData} />
      {data?.aAvoidPlayerFan?.length > 0 && <AvoidPlayer avoidPlayers={data?.aAvoidPlayerFan} />}
      <FantasyTeam
        fantasystyles={fantasyStyles}
        type={fantasyArticleData?.ePlatformType}
        teamData={fantasyArticleData?.aLeague}
        matchData={fantasyArticleData?.oMatch}
        updatedTime={dateCheck(data?.dUpdated)}
      />

      {/* {!isMobile && <TipsNote /> } */}
      {/* {fantasyArticleData?.aSeriesCategory} */}
      <div className={`${fantasyStyles.tagList} d-flex flex-lg-wrap pb-2 pb-lg-0`}>
        {fantasyArticleData?.aSeriesCategory?.map(cat => (
          <div key={cat?.oSeo?.sSlug}>
            <CustomLink href={'/' + cat?.oSeo?.sSlug} prefetch={false}>
              <a target="_blank">
                <Badge bg="primary">{cat?.sName}</Badge>
              </a>
            </CustomLink>
          </div>
        ))}

        {fantasyArticleData?.aTeamTag?.map(team => (
          <div key={team?.oSeo?.sSlug}>
            <CustomLink href={'/' + team?.oSeo?.sSlug} prefetch={false}>
              <a target="_blank">
                <Badge bg="primary">{team?.sName}</Badge>
              </a>
            </CustomLink>
          </div>
        ))}

        {fantasyArticleData?.aPlayerTag?.map(player => (
          <div key={player?.oSeo?.sSlug}>
            <CustomLink href={'/' + player?.oSeo?.sSlug} prefetch={false}>
              <a target="_blank">
                <Badge bg="primary">{player?.sName}</Badge>
              </a>
            </CustomLink>
          </div>
        ))}

        {fantasyArticleData?.aTagsData?.map(tag => (
          <div key={tag?.oSeo?.sSlug}>
            <CustomLink href={'/' + tag?.oSeo?.sSlug} prefetch={false}>
              <a target="_blank">
                <Badge bg="primary">{tag?.sName}</Badge>
              </a>
            </CustomLink>
          </div>
        ))}
      </div>
    </div>
  )
}

FantasyArticleContent.propTypes = {
  data: PropTypes.object,
  playingXI: PropTypes.object,
  playerData: PropTypes.array,
  match: PropTypes.object,
  seriesData: PropTypes.array,
  isMobile: PropTypes.bool
}

export default FantasyArticleContent
